import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import Icon from '../../../shared/components/Icon';
import BrandMark from '../../../shared/components/BrandMark';
import { loginApi, ssoLoginApi } from '../../../api/carbonApi';
import { applyApiFieldErrors, translateMessage } from '../../../shared/api';
import msalInstance, { loginRequest } from '../../../auth/msalInstance';
import { getCopyrightYear } from '../model/loginPresentation';
import { clearStaleMsalInteractionStatus } from '../model/msalInteraction';
import '../../../styles/common.css';
import '../styles/login.css';

export default function LoginScreen({ onLogin, sessionExpired }) {
  const { t } = useTranslation();
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading,  setLoading]  = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [error,    setError]    = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const mountedRef = useRef(true);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  async function submit(e) {
    if (e) e.preventDefault();
    setError('');
    setFieldErrors({});
    if (!email.trim()) {
      setFieldErrors({ email: t('login.emailRequired') });
      return;
    }
    if (password.length < 4) {
      setFieldErrors({ password: t('login.passwordTooShort') });
      return;
    }
    setLoading(true);
    try {
      const data = await loginApi(email.trim(), password);
      onLogin({ user: data.user, token: data.access_token, remember });
    } catch (err) {
      if (mountedRef.current) {
        const nextFieldErrors = {};
        let nextRootError = '';
        const hasFieldErrors = applyApiFieldErrors(err, (field, detail) => {
          if (field === 'email' || field === 'password') {
            nextFieldErrors[field] = detail.message;
          } else {
            if (!nextRootError) nextRootError = detail.message;
          }
        }, t);
        if (hasFieldErrors) {
          setFieldErrors(nextFieldErrors);
          setError(nextRootError);
        } else {
          setError(err?.name === 'ApiError'
            ? translateMessage(err.message, err.status, err.params)
            : t('login.failed'));
        }
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }

  async function ssoSignIn() {
    setError('');
    setFieldErrors({});
    setSsoLoading(true);
    try {
      const response = await msalInstance.loginPopup(loginRequest);
      const data = await ssoLoginApi(response.accessToken);
      onLogin({ user: data.user, token: data.access_token, remember: false });
    } catch (err) {
      if (err.errorCode === 'interaction_in_progress') {
        clearStaleMsalInteractionStatus();
        if (mountedRef.current) setError(t('login.ssoInterrupted'));
      } else if (err.errorCode !== 'user_cancelled') {
        if (mountedRef.current) {
          setError(err?.name === 'ApiError'
            ? translateMessage(err.message, err.status, err.params)
            : t('login.ssoError'));
        }
      }
    } finally {
      if (mountedRef.current) setSsoLoading(false);
    }
  }

  const isProcessing = loading || ssoLoading;
  const copyrightYear = getCopyrightYear();

  return (
    <div className="overlay-full login-page">
      <div className="overlay-card-wrap login-page__content">
        {/* Brand */}
        <div className="login-brand">
          <div className="login-brand__mark">
            <BrandMark size={40} />
          </div>
          <div>
            <div className="login-brand__title">{t('breadcrumb.appName')}</div>
            <div className="login-brand__subtitle">{t('login.brandSubtitle')}</div>
          </div>
        </div>

        {/* Card */}
        <div className="login-card">
          {sessionExpired && (
            <div className="login-alert">
              {t('login.sessionExpired')}
            </div>
          )}

          {ssoLoading ? (
            <div className="login-sso-loading">
              <CircularProgress
                aria-label={t('login.ssoLoading')}
                color="primary"
                size={38}
                thickness={4}
              />
            </div>
          ) : (
            <>
              <h1 className="login-form__title">{t('login.title')}</h1>
              <p className="login-form__subtitle">
                {t('login.subtitle')}
              </p>

              {/* Microsoft SSO button */}
              <button
                className="sso-btn login-sso-btn"
                onClick={ssoSignIn}
                type="button"
                disabled={isProcessing}
              >
                <svg width="16" height="16" viewBox="0 0 23 23" className="login-sso-btn__icon">
                  <path fill="#f25022" d="M1 1h10v10H1z"/>
                  <path fill="#7fba00" d="M12 1h10v10H12z"/>
                  <path fill="#00a4ef" d="M1 12h10v10H1z"/>
                  <path fill="#ffb900" d="M12 12h10v10H12z"/>
                </svg>
                {t('login.ssoButton')}
              </button>

              <div className="divider"><span>{t('login.or')}</span></div>

              <form className="login-form" onSubmit={submit}>
                <label className="field">
                  <span className="field-label">{t('login.email')}</span>
                  <input
                    type="email"
                    className="field-input"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setFieldErrors(current => ({ ...current, email: '' }));
                      setError('');
                    }}
                    placeholder={t('login.emailPlaceholder')}
                    autoComplete="username"
                    autoFocus
                    aria-invalid={Boolean(fieldErrors.email)}
                    aria-describedby={fieldErrors.email ? 'login-email-error' : undefined}
                  />
                  {fieldErrors.email && (
                    <span id="login-email-error" className="login-error" role="alert">
                      {fieldErrors.email}
                    </span>
                  )}
                </label>

                <label className="field">
                  <span className="field-label">{t('login.password')}</span>
                  <div className="field-input-group">
                    <input
                      type={showPw ? 'text' : 'password'}
                      className="field-input"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setFieldErrors(current => ({ ...current, password: '' }));
                        setError('');
                      }}
                      placeholder={t('login.passwordPlaceholder')}
                      autoComplete="current-password"
                      aria-invalid={Boolean(fieldErrors.password)}
                      aria-describedby={fieldErrors.password ? 'login-password-error' : undefined}
                    />
                    <button type="button" className="pw-toggle" onClick={() => setShowPw(s => !s)} aria-label={t('login.togglePassword')}>
                      <Icon name={showPw ? 'arrow-down' : 'arrow-up'} size={13} />
                    </button>
                  </div>
                  {fieldErrors.password && (
                    <span id="login-password-error" className="login-error" role="alert">
                      {fieldErrors.password}
                    </span>
                  )}
                </label>

                <label className="checkbox-row">
                  <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} />
                  <span>{t('login.rememberMe')}</span>
                </label>

                {error && <div className="login-error">{error}</div>}

                <button type="submit" className="login-submit" disabled={isProcessing}>
                  {loading
                    ? <CircularProgress aria-label={t('login.signingIn')} color="inherit" size={18} thickness={5} />
                    : <>{t('login.signIn')} <Icon name="chevron-right" size={14} stroke={2.2} /></>}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="login__footer">
          © {copyrightYear} {t('breadcrumb.appName')}
        </p>
      </div>
    </div>
  );
}
